import type {
  LayoutGroupId,
  LayoutPreset,
  LayoutPresetStore,
  LayoutSettings,
  LayoutTransform,
} from "./layout-presets";

type LayoutTransformTuple = [x: number, y: number, scale: number];
type LayoutTransformTuples = Record<LayoutGroupId, LayoutTransformTuple>;

const presetGroupIds: LayoutGroupId[] = [
  "logo",
  "headerDownloads",
  "heading",
  "openingPack",
  "packs",
  "rewards",
  "garden",
  "tagline",
  "storeButtons",
  "footer",
];

function createLayout(tuples: LayoutTransformTuples): LayoutSettings {
  return Object.fromEntries(
    presetGroupIds.map((groupId) => {
      const [x, y, scale] = tuples[groupId];
      return [groupId, { x, y, scale } satisfies LayoutTransform];
    }),
  ) as LayoutSettings;
}

function createPreset(
  id: string,
  width: number,
  height: number,
  tuples: LayoutTransformTuples,
): LayoutPreset {
  return {
    id,
    name: `${width} × ${height}`,
    width,
    height,
    layout: createLayout(tuples),
  };
}

export const builtInLayoutPresetStore: LayoutPresetStore = {
  version: 1,
  presets: [
    createPreset("screen-440x956-1784878224799", 440, 956, {
      logo: [0, 0, 1],
      headerDownloads: [0, 0, 1],
      heading: [0, 15, 1],
      openingPack: [0, -20, 1],
      packs: [0, 15, 1],
      rewards: [0, -3, 1],
      garden: [0, 32, 1.08],
      tagline: [0, -15, 1],
      storeButtons: [0, -15, 0.92],
      footer: [0, -85, 1],
    }),
    createPreset("screen-402x874-1784878325794", 402, 874, {
      logo: [0, 0, 1],
      headerDownloads: [0, 0, 1],
      heading: [0, 15, 1],
      openingPack: [0, -20, 1],
      packs: [0, 15, 1],
      rewards: [0, -3, 1],
      garden: [0, 32, 1.08],
      tagline: [0, -3, 1],
      storeButtons: [0, -3, 0.92],
      footer: [0, -72, 1],
    }),
    createPreset("screen-375x844-1784878374045", 375, 844, {
      logo: [0, 0, 1],
      headerDownloads: [0, 0, 1],
      heading: [0, -20, 1],
      openingPack: [0, -20, 1],
      packs: [0, 15, 1.17],
      rewards: [0, 15, 1.23],
      garden: [0, 32, 1.08],
      tagline: [0, 50, 1],
      storeButtons: [0, 50, 0.92],
      footer: [0, -22, 1],
    }),
    createPreset("screen-375x667-1784878411911", 375, 667, {
      logo: [0, 0, 1],
      headerDownloads: [0, 0, 1],
      heading: [0, -20, 1],
      openingPack: [0, -20, 1],
      packs: [0, 15, 1.17],
      rewards: [0, 15, 1.23],
      garden: [0, 32, 1.08],
      tagline: [0, 50, 1],
      storeButtons: [0, 50, 0.92],
      footer: [0, -22, 1],
    }),
    createPreset("screen-744x1133-1784878847592", 744, 1133, {
      logo: [33, -13, 1.71],
      headerDownloads: [0, 0, 1.36],
      heading: [0, 0, 1.17],
      openingPack: [0, 0, 1],
      packs: [0, 70, 1.33],
      rewards: [0, 35, 1.17],
      garden: [0, 52, 1.08],
      tagline: [0, 0, 1.55],
      storeButtons: [0, -17, 0.92],
      footer: [0, -2, 1],
    }),
    createPreset("screen-820x1180-1784878866879", 820, 1180, {
      logo: [33, -13, 1.71],
      headerDownloads: [0, 0, 1.36],
      heading: [0, 0, 1.17],
      openingPack: [0, 0, 1],
      packs: [0, 70, 1.33],
      rewards: [0, 0, 1.17],
      garden: [0, 52, 1.08],
      tagline: [0, -17, 1.55],
      storeButtons: [0, -52, 0.92],
      footer: [0, -2, 1],
    }),
    createPreset("screen-1366x768-1784878959248", 1366, 768, {
      logo: [-17, 0, 0.92],
      headerDownloads: [0, 0, 1.2],
      heading: [0, 0, 1],
      openingPack: [0, 17, 1.01],
      packs: [0, 0, 0.8899999999999999],
      rewards: [0, 0, 0.98],
      garden: [0, 17, 1.08],
      tagline: [0, -7, 1],
      storeButtons: [0, 0, 0.98],
      footer: [0, 0, 1],
    }),
    createPreset("screen-1440x900-1784879117548", 1440, 900, {
      logo: [-17, 0, 0.92],
      headerDownloads: [0, 0, 1.2],
      heading: [0, 0, 1],
      openingPack: [0, 17, 1.01],
      packs: [0, 0, 0.9799999999999999],
      rewards: [0, -17, 0.92],
      garden: [0, 17, 1.08],
      tagline: [0, 0, 1],
      storeButtons: [0, 11, 0.98],
      footer: [0, 0, 1],
    }),
  ],
};
